/**
 * ScheduleMessagePickerModal — модалка выбора даты/времени отправки.
 * Возвращает UTC ISO строку (без tz) — backend трактует как UTC.
 *
 * Пресеты: «Через 1 час», «Сегодня 18:00», «Завтра 09:00», «Завтра 18:00»,
 * «Через неделю», + произвольная дата/время.
 */
import { useMemo, useState, useEffect } from "react";

function toUtcIso(local: Date): string {
  // Backend хранит без tz — считает UTC. Мы даём фронт-Date как локальное
  // время, переводим в UTC ISO (обрезаем 'Z' — backend fromisoformat примет).
  return local.toISOString().replace(/\.\d{3}Z$/, "").replace(/Z$/, "");
}

function addHours(d: Date, h: number): Date {
  const n = new Date(d.getTime());
  n.setHours(n.getHours() + h);
  return n;
}

function addDays(d: Date, dd: number): Date {
  const n = new Date(d.getTime());
  n.setDate(n.getDate() + dd);
  return n;
}

function setLocalTime(d: Date, h: number, m: number): Date {
  const n = new Date(d.getTime());
  n.setHours(h, m, 0, 0);
  return n;
}

function fmtLocal(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ScheduleMessagePickerModal({ onSelect, onClose, initial }: {
  onSelect: (utcIsoNoTz: string) => void;
  onClose: () => void;
  /** Если редактируем существующее — начальное значение как UTC ISO без tz. */
  initial?: string;
}) {
  const now = useMemo(() => new Date(), []);
  const [customDate, setCustomDate] = useState<string>(() => {
    const d = initial ? new Date(initial + "Z") : addHours(now, 1);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });
  const [customTime, setCustomTime] = useState<string>(() => {
    const d = initial ? new Date(initial + "Z") : addHours(now, 1);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const presets = useMemo(() => {
    const tomorrow = addDays(now, 1);
    return [
      { label: "Через 1 час", d: addHours(now, 1) },
      { label: "Сегодня 18:00", d: setLocalTime(now, 18, 0) },
      { label: "Завтра 09:00", d: setLocalTime(tomorrow, 9, 0) },
      { label: "Завтра 18:00", d: setLocalTime(tomorrow, 18, 0) },
      { label: "Через 3 дня, 09:00", d: setLocalTime(addDays(now, 3), 9, 0) },
      { label: "Через неделю", d: addDays(now, 7) },
    ].filter(p => p.d.getTime() - now.getTime() > 60_000);
  }, [now]);

  function submitCustom() {
    if (!customDate || !customTime) return;
    const [y, mo, d] = customDate.split("-").map(Number);
    const [h, mi] = customTime.split(":").map(Number);
    const local = new Date(y, mo - 1, d, h, mi, 0, 0);
    if (local.getTime() - Date.now() < 30_000) {
      alert("Время должно быть в будущем (минимум +30 сек)");
      return;
    }
    onSelect(toUtcIso(local));
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
        zIndex: 10002, display: "grid", placeItems: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, padding: 20,
          minWidth: 320, maxWidth: 480, width: "100%",
          boxShadow: "0 24px 48px rgba(0,0,0,0.24)",
        }}
      >
        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 14, color: "var(--ink)" }}>
          Отложить отправку
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => onSelect(toUtcIso(p.d))}
              style={{
                padding: "10px 12px", border: "1px solid var(--line, #e8e8e8)",
                background: "var(--paper-2, #f7f7f7)", borderRadius: 10,
                cursor: "pointer", textAlign: "left", fontFamily: "inherit",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(66,165,245,0.08)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "var(--paper-2, #f7f7f7)"; }}
            >
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink, #131313)" }}>{p.label}</div>
              <div style={{ fontSize: 11.5, color: "var(--mute, #999)", marginTop: 2 }}>
                {fmtLocal(p.d)}
              </div>
            </button>
          ))}
        </div>

        <div style={{ borderTop: "1px solid var(--line, #e8e8e8)", paddingTop: 14, marginTop: 4 }}>
          <div style={{ fontSize: 12.5, color: "var(--mute, #888)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Или выберите дату и время
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              style={{ flex: 1, padding: "9px 10px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14 }}
            />
            <input
              type="time"
              value={customTime}
              onChange={(e) => setCustomTime(e.target.value)}
              style={{ width: 120, padding: "9px 10px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14 }}
            />
          </div>
          <button
            type="button"
            onClick={submitCustom}
            style={{
              width: "100%", padding: "11px 14px", background: "var(--tg-accent, #42a5f5)",
              color: "#fff", border: "none", borderRadius: 10, cursor: "pointer",
              fontSize: 14, fontWeight: 600, fontFamily: "inherit",
            }}
          >
            Отложить на выбранное время
          </button>
        </div>

        <button
          type="button"
          onClick={onClose}
          style={{
            width: "100%", padding: "10px 14px", background: "transparent",
            color: "var(--mute, #888)", border: "none", cursor: "pointer",
            fontSize: 13, marginTop: 6, fontFamily: "inherit",
          }}
        >
          Отмена
        </button>
      </div>
    </div>
  );
}
